import { computed, onBeforeUnmount, ref, watch } from 'vue'

import AttributionViewModel from '@arcgis/core/widgets/Attribution/AttributionViewModel'

import useView from '@tsyl/karonte/lib/composables/useView'

// Reuses ArcGIS's native attribution resolution (layer `copyright`, portal item access information and
// Esri dynamic attribution data) without its widget DOM, which karonte disables and does not style.
// Per-layer text is set in the layer config through `extraArcgisParameter.copyright`.
export default function useMapAttribution(idMap?: string) {
  const view = useView(idMap).getReactiveView()
  const sources = ref<string[]>([])

  let viewModel: AttributionViewModel | undefined
  let itemsHandle: IHandle | undefined

  function destroyViewModel() {
    // Collection change events are delivered asynchronously: detach first so a late event from the
    // destroyed view model can't overwrite the sources of the new one
    itemsHandle?.remove()
    itemsHandle = undefined
    viewModel?.destroy()
    viewModel = undefined
    sources.value = []
  }

  watch(
    () => view.value?.nativeView as __esri.MapView | undefined,
    (nativeView) => {
      destroyViewModel()
      if (!nativeView) return

      const vm = new AttributionViewModel({ view: nativeView })
      const syncSources = () => {
        // Several layers can share a source; show each one once, in layer order.
        sources.value = [...new Set(vm.items.toArray().map((item) => item.text))]
      }
      itemsHandle = vm.items.on('change', syncSources)
      syncSources()
      viewModel = vm
    },
    { immediate: true }
  )

  onBeforeUnmount(destroyViewModel)

  return {
    sourcesText: computed(() => sources.value.join(' | '))
  }
}
